import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { SetupPanel } from "@/components/setup-panel";
import { DataTable, type DataColumn } from "@/components/data-table";
import { BarList } from "@/components/analytics-ui";
import { ChartCard } from "@/components/charts/chart-card";
import { DonutChart } from "@/components/charts/donut-chart";
import { HighlightRow } from "@/components/charts/highlight-row";
import { RankList } from "@/components/charts/rank-list";
import { colorAt } from "@/components/charts/palette";
import { TrackingNote } from "@/components/tracking-note";
import { SkHeader, SkPanel, SkTiles } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { DEFAULT_PRESET } from "@shared/analytics-range";
import { withRange } from "@shared/store-report";
import { CHANNELS } from "@shared/channels";
import { fmtDayTime, fmtNum, fmtPct } from "@shared/format";
import type { ShoppersPage } from "@shared/pages/shoppers";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/_app/shoppers/")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<ShoppersPage>("shoppers", deps));
  },
  component: ShoppersRoute,
});

const COLUMNS: DataColumn[] = [
  { key: "when", label: "Ordered", sortBy: "at" },
  { key: "name", label: "Order" },
  { key: "customer", label: "Customer", hint: "Whether the order is tied to a Shopify customer. The journey shows their name and email." },
  { key: "firstChannel", label: "First visit from", hint: "Where the shopper's first recorded visit came from (first touch)." },
  { key: "lastChannel", label: "Bought after", hint: "Where the visit they bought in came from (last touch)." },
  { key: "visits", label: "Visits", format: "num", align: "right", hint: "Visits from the first recorded one up to the order." },
  { key: "days", label: "Days", format: "num", align: "right", hint: "Days from the first recorded visit to the order." },
  { key: "productsViewed", label: "Products seen", format: "num", align: "right" },
  { key: "netSales", label: "Shopify sales", format: "usd2", align: "right" },
  { key: "journey", label: "", format: "journey", journey: "order" },
];

const channelColor = (c: string) => colorAt(Math.max(0, (CHANNELS as readonly string[]).indexOf(c)));

const plural = (n: number, one: string, many = `${one}s`) => `${fmtNum(n)} ${n === 1 ? one : many}`;

// Journeys: header, four highlights, the two touch donuts, the trio, the table.
function Skeleton() {
  return (
    <div className="page-shell">
      <SkHeader />
      <SkTiles count={4} />
      <div className="mt-4 grid gap-4 @3xl:grid-cols-2">
        <SkPanel />
        <SkPanel />
      </div>
      <div className="mt-4 grid gap-4 @3xl:grid-cols-2 @6xl:grid-cols-3">
        <SkPanel />
        <SkPanel />
        <SkPanel className="@3xl:col-span-2 @6xl:col-span-1" />
      </div>
      <SkPanel className="mt-4" bodyClassName="h-72" />
    </div>
  );
}

function ShoppersRoute() {
  useTitle("Journeys");
  const search = Route.useSearch();
  return (
    <PageQuery<ShoppersPage> name="shoppers" search={search} skeleton={<Skeleton />}>
      {(page) => <ShopperJourneys page={page} />}
    </PageQuery>
  );
}

function ShopperJourneys({ page }: { page: ShoppersPage }) {
  const { range: r, status, journeys: j } = page;
  const header = (
    <PageHeader
      title="Journeys"
      info="What did shoppers look at, and how many visits did it take, before they bought?"
      actions={<DateRangeFilter presets={r.presets} defaultKey={DEFAULT_PRESET} />}
    />
  );

  if (!j) {
    return (
      <div className="page-shell">
        {header}
        <SetupPanel
          title="Add the pixel in Shopify"
          href={page.canManage ? "/settings#shopper-tracking" : undefined}
          linkLabel="Open the pixel code in Settings"
        >
          <p>
            Journeys come from a small piece of code (a custom pixel) that the store adds in Shopify admin under Settings, Customer events. Once it is
            saved and connected, every page view, product view, cart change and checkout shows up here within a minute.
          </p>
          <p className="mt-2">
            {status.keySet
              ? "The dashboard is ready to receive events. Copy the pixel from Settings and paste it into Shopify."
              : "The dashboard's pixel key is not set yet, so events would be turned away. Digital King sets it before the pixel goes in."}
          </p>
        </SetupPanel>
      </div>
    );
  }

  const coverage = j.onlineOrders ? j.trackedOrders / j.onlineOrders : null;
  const rows = j.recent.map((o) => ({
    id: o.orderId,
    at: o.at,
    when: fmtDayTime(o.at),
    name: o.name,
    customer: o.hasCustomer ? "Yes" : "Guest",
    firstChannel: o.firstChannel,
    lastChannel: o.lastChannel,
    visits: o.visits,
    days: o.days,
    productsViewed: o.productsViewed,
    netSales: o.netSales,
    journey: o.orderId,
  }));

  return (
    <div className="page-shell">
      {header}
      <TrackingNote status={status} />

      <HighlightRow
        items={[
          {
            label: "Orders with a journey",
            value: fmtNum(j.trackedOrders),
            sub: j.onlineOrders ? `of ${plural(j.onlineOrders, "online order")} (${fmtPct(coverage)})` : "No online orders in this range",
          },
          { label: "Visits before buying", value: j.medianVisits !== null ? fmtNum(j.medianVisits) : "–", sub: "Typical (median) order" },
          { label: "Days before buying", value: j.medianDays !== null ? fmtNum(j.medianDays) : "–", sub: "From the first recorded visit" },
          { label: "Bought on the first visit", value: fmtPct(j.firstVisitShare), sub: "Share of orders with a journey" },
        ]}
      />

      <div className="mt-4 grid gap-4 @3xl:grid-cols-2">
        <ChartCard title="How buyers first found the store" hint="First touch: the channel of each buyer's first recorded visit. From the shopper pixel.">
          <DonutChart
            format="number"
            centerLabel="orders"
            slices={j.firstTouch.map((c) => ({ key: c.channel, label: c.label, value: c.orders, color: channelColor(c.channel) }))}
          />
        </ChartCard>
        <ChartCard title="The visit they bought in" hint="Last touch: the channel of the visit with the order. From the shopper pixel.">
          <DonutChart
            format="number"
            centerLabel="orders"
            slices={j.lastTouch.map((c) => ({ key: c.channel, label: c.label, value: c.orders, color: channelColor(c.channel) }))}
          />
        </ChartCard>
      </div>

      <div className="mt-4 grid gap-4 @3xl:grid-cols-2 @6xl:grid-cols-3">
        <ChartCard title="Looked at before buying" hint="Buyers who viewed each product before their order. From the shopper pixel.">
          <RankList
            format="number"
            items={j.viewedBeforeBuying.map((p) => ({
              label: p.title,
              value: p.count,
              href: withRange(`/products/${p.productId}`, r.params),
              imageUrl: p.imageUrl,
            }))}
            emptyText="No product views before an order in this range."
          />
        </ChartCard>
        <ChartCard title="Looked at and left" hint="Shoppers who viewed each product and have not ordered anything. From the shopper pixel.">
          <RankList
            format="number"
            items={j.viewedAndLeft.map((p) => ({
              label: p.title,
              value: p.count,
              href: withRange(`/products/${p.productId}`, r.params),
              imageUrl: p.imageUrl,
            }))}
            emptyText="No product views in this range."
          />
        </ChartCard>
        <ChartCard
          className="@3xl:col-span-2 @6xl:col-span-1"
          title="Where buyers landed"
          hint="The first page of the visit each order was placed in. From the shopper pixel."
        >
          <BarList toneClass="bg-primary" items={j.buyerLandings.map((l) => ({ label: l.path, count: l.orders }))} emptyText="No orders with a journey in this range." />
        </ChartCard>
      </div>

      <ChartCard
        className="mt-4"
        title="Recent orders with a journey"
        hint="Orders the shopper pixel saw completed, newest first. Eastern time. Open a journey to see every visit before the order."
      >
        <DataTable columns={COLUMNS} rows={rows} rowKey="id" defaultSort="when" pageSize={15} emptyText="No orders with a journey in this range." />
      </ChartCard>
    </div>
  );
}
